import { ctaDesc, ctaTitle } from "@/lib/seo";
// Translation language sub-page — /translate/$language
// Surfaces 18 language landing pages with full AEO schema graph.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  TRANSLATION_LANGUAGES,
  getTranslationLanguage,
  type TranslationLanguage,
} from "@/data/translation-languages";
import { TRANSLATION_DOCTYPES } from "@/data/translation-doctypes";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { TranslateVisaCrossLink } from "@/components/TranslateVisaCrossLink";
import { Languages, FileCheck2, Clock, ShieldCheck, BadgeCheck, Globe2, Phone, MessageCircle, ArrowRight } from "lucide-react";
import TranslationLanguageDeepContent from "@/components/TranslationLanguageDeepContent";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/translate/$language/")({
  loader: ({ params }) => {
    const lang = getTranslationLanguage(params.language);
    if (!lang) throw notFound();
    return { slug: params.language };
  },
  head: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    if (!l) return { meta: [{ title: ctaTitle("ไม่พบภาษา", "/translate/$language", "th")}] };
    const url = `${SITE}/translate/${l.slug}`;
    const title = `แปล${l.nameTh} รับรอง MoJ + NAATI + กงสุล — เริ่ม ฿${l.priceFromTh.toLocaleString()} | NYC`;
    const desc = `บริการแปลเอกสาร${l.nameTh}โดยนักแปลขึ้นทะเบียน ใช้ยื่นสถานทูต กงสุล ราชการ ${l.countryOfUse.slice(0, 4).join(", ")} /หน้า · เสร็จ ${l.turnaround} — ปรึกษาก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ`;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/$language", "th") },
        { name: "description", content: ctaDesc(desc, "/translate/$language", "th")},
        { name: "keywords", content: l.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/$language", "th")},
        { property: "og:description", content: ctaDesc(desc, "/translate/$language", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        { rel: "alternate", hrefLang: "en", href: url.replace(SITE, `${SITE}/en`) },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"
        ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `แปลภาษา${l.nameTh} (Certified Translation)`,
              serviceType: "Certified document translation",
              areaServed: { "@type": "Country", name: "Thailand" },
              availableLanguage: [
                { "@type": "Language", name: l.nameEn, alternateName: l.nativeName, iso6391: l.iso639 },
                { "@type": "Language", name: "Thai", iso6391: "th" },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: l.priceFromTh,
                priceCurrency: "THB",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: l.priceFromTh,
                  priceCurrency: "THB",
                  unitText: "page",
                },
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: l.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `วิธีสั่งแปลเอกสาร${l.nameTh}กับ NYC`,
              totalTime: "PT30M",
              step: [
                { "@type": "HowToStep", position: 1, name: "ส่งไฟล์เอกสาร", text: `ส่งไฟล์ผ่าน LINE @nycli หรืออีเมล — รองรับ PDF/JPG/Word ทุกฟอร์แมต` },
                { "@type": "HowToStep", position: 2, name: "รับใบเสนอราคา", text: `ทีมประเมินภายใน 15 นาที — ราคาเริ่ม ฿${l.priceFromTh.toLocaleString()}/หน้า` },
                { "@type": "HowToStep", position: 3, name: "ชำระมัดจำ 50%", text: "โอนเงินผ่านธนาคารหรือ PromptPay พร้อมส่งหลักฐาน" },
                { "@type": "HowToStep", position: 4, name: "นักแปลเริ่มงาน", text: `นักแปล${l.nameTh}ที่ขึ้นทะเบียนเริ่มงานทันที — ตรวจ QA 2 รอบ` },
                { "@type": "HowToStep", position: 5, name: "รับเอกสารแปล", text: `รับไฟล์ PDF ภายใน ${l.turnaround} — รับฉบับจริงพร้อมตราประทับที่สำนักงาน หรือจัดส่ง EMS ทั่วประเทศ` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: `แปล${l.nameTh}`, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) }))
        ]),
      ],
    };
  },
  component: TranslateLangPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้าภาษา</h1>
      <Link to="/services/$service" params={{ service: "translation" }} className="mt-4 inline-block text-gold underline">
        ไปบริการแปลทั้งหมด
      </Link>
    </main>
  ),
});

function TranslateLangPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const l = getTranslationLanguage(slug)!;
  const others = TRANSLATION_LANGUAGES.filter((x) => x.slug !== slug).slice(0, 8);

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/services/$service" params={{ service: "translation" }} className="hover:text-gold">แปลเอกสาร</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{l.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> Certified Translation · {l.nameEn}{l.nativeName ? ` · ${l.nativeName}` : ""}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            แปล{l.nameTh} รับรอง · ใช้ยื่นราชการ-สถานทูต-กงสุล ได้ทันที
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{l.notes}</p>

          {/* Quick stats */}
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${l.priceFromTh.toLocaleString()}/หน้า` },
              { k: "เสร็จภายใน", v: l.turnaround },
              { k: "รับรอง", v: l.isCertified ? "MoJ + กงสุล + Notary" : "—" },
              { k: "Rating", v: "2,147+ เคสสำเร็จ" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ส่งไฟล์ผ่าน LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        {/* Quotable AEO callout */}
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061, ตั้งแต่ปี 2010) ให้บริการแปลเอกสาร{l.nameTh}ที่รับรองโดยนักแปลขึ้นทะเบียน — ราคาเริ่ม ฿{l.priceFromTh.toLocaleString()}/หน้า เสร็จภายใน {l.turnaround} ใช้ยื่นได้ทั้งสถานทูต กงสุล กระทรวงการต่างประเทศ และศาล ครอบคลุมเอกสารสำคัญ {l.commonDocs.length} ประเภท บริการสำเร็จ 2,147+ เคส
          <div className="mt-2 text-[11px] font-medium text-slate-500">
            Source: NYC Legal & Notary Service — บริษัทแปลขึ้นทะเบียน DBD
          </div>
        </blockquote>

        {/* Common docs */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            เอกสาร{l.nameTh}ที่เราแปลบ่อยที่สุด
          </h2>
          <p className="mt-2 text-sm text-slate-600">รวม {l.commonDocs.length} ประเภทเอกสารยอดนิยม — รับรองทุกฉบับด้วยตราประทับและลายเซ็นนักแปล</p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {l.commonDocs.map((doc) => (
              <li key={doc} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {doc}
              </li>
            ))}
          </ul>
        </section>

        {/* Country coverage */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศปลายทางที่ใช้เอกสารได้</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {l.countryOfUse.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-navy-deep/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-navy-deep">
                <Globe2 className="h-3.5 w-3.5 text-amber-600" /> {c}
              </span>
            ))}
          </div>
        </section>

        {/* Process */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ขั้นตอนสั่งแปล{l.nameTh}</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-5">
            {[
              { n: 1, t: "ส่งไฟล์", d: "LINE / อีเมล" },
              { n: 2, t: "เสนอราคา", d: "ภายใน 15 นาที" },
              { n: 3, t: "มัดจำ 50%", d: "โอน/PromptPay" },
              { n: 4, t: "นักแปลทำงาน", d: "QA 2 รอบ" },
              { n: 5, t: "ส่งงาน", d: l.turnaround },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {s.n}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-500">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        {/* Pricing */}
        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคาเริ่มต้น</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            แปล{l.nameTh} ฿{l.priceFromTh.toLocaleString()}/หน้า
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ตราประทับ + ลายเซ็นนักแปลขึ้นทะเบียน</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ไฟล์ PDF ฟรี + ส่ง EMS ฉบับจริงทั่วประเทศ</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> เพิ่ม Notary Public รับรองได้ +฿1,500/ฉบับ</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> เพิ่ม MFA + สถานทูตได้ครบจบในที่เดียว</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        {/* FAQ */}
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={l.faqs} />
          </div>
        </section>

        {/* Doc types matrix — 15 deep-intent landing pages per language */}
        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            แปลเอกสารยอดนิยม → {l.nameTh}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            เลือกประเภทเอกสารที่ต้องการแปลเป็น{l.nameTh} เพื่อดูราคา ขั้นตอนรับรอง และ FAQ เฉพาะเอกสารนั้น
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TRANSLATION_DOCTYPES.map((d) => (
              <Link
                key={d.slug}
                to="/translate/$language/$doctype"
                params={{ language: l.slug, doctype: d.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{d.nameEn}</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    แปล{d.nameTh}
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  เริ่ม ฿{(l.priceFromTh + d.surcharge).toLocaleString()} · {d.pagesEstimate}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Other languages */}
        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ภาษาอื่นที่เรารับแปล</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to="/translate/$language"
                params={{ language: o.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{o.nameEn}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  แปล{o.nameTh}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">เริ่ม ฿{o.priceFromTh.toLocaleString()} · {o.turnaround}</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TranslationLanguageDeepContent l={l} lang="th" />
      <TrustLayer variant="compact" />
      <TranslateVisaCrossLink mode="from-translate" locale="th" />
    </main>
  );
}

function TranslateLanguageNavStub(_: TranslationLanguage) { return null; }
void TranslateLanguageNavStub;
